import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../config/supabase';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Badge,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  Paper,
  ButtonGroup,
  Stack,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  TrendingUp,
  ArrowBack,
  LocalFireDepartment,
  Psychology,
  Favorite,
  Timeline,
  EmojiEvents,
  Star,
  Bolt,
  CalendarToday,
  BarChart,
  PieChart,
  TrendingDown,
  Assignment,
  EmojiEventsOutlined,
  DateRange,
  FilterList,
  Assessment,
  MoreVert,
  BookOutlined,
  FlashOn,
  Analytics,
  Settings,
  Logout,
  MonetizationOn,
  People,
  SportsEsports,
  EmojiObjects,
  Archive
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart as RechartsBarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';

const ProgressDashboard = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    journals: [],
    meditations: [],
    conversations: [],
    metrics: {}
  });
  
  // Date range and view state
  const [dateRange, setDateRange] = useState('week'); // day, week, month, year, custom
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0: Overview, 1: Charts, 2: Achievements
  const [chartType, setChartType] = useState('line'); // line, bar, area
  
  // Badge and challenge system
  const [achievements, setAchievements] = useState([]);
  const [activeChallenges, setActiveChallenges] = useState([]);

  // Get date range for filtering
  const getDateRange = useCallback(() => {
    const now = new Date();
    let startDate, endDate = now;

    switch (dateRange) {
      case 'day':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'year':
        startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      case 'custom':
        startDate = customStartDate ? new Date(customStartDate) : new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = customEndDate ? new Date(customEndDate) : now;
        break;
      default:
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    }

    return { startDate, endDate };
  }, [dateRange, customStartDate, customEndDate]);

  // Generate chart data based on date range
  const generateChartData = useCallback(() => {
    const { startDate, endDate } = getDateRange();
    const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const chartData = [];

    for (let i = days; i >= 0; i--) {
      const date = new Date(endDate.getTime() - i * 24 * 60 * 60 * 1000);
      const dateStr = date.toISOString().split('T')[0];
      
      const dayJournals = data.journals.filter(j => j.entry_date === dateStr).length;
      const dayMeditations = data.meditations.filter(m => m.created_at?.split('T')[0] === dateStr).length;
      const dayConversations = data.conversations.filter(c => c.created_at?.split('T')[0] === dateStr).length;
      
      const dailyXP = dayJournals * 50 + dayMeditations * 30 + dayConversations * 20;
      
      chartData.push({
        date: dateStr,
        displayDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        journals: dayJournals,
        meditations: dayMeditations,
        conversations: dayConversations,
        totalXP: dailyXP,
        totalActivities: dayJournals + dayMeditations + dayConversations
      });
    }

    return chartData;
  }, [data, getDateRange]);

  // Achievement system
  const checkAchievements = useCallback((metrics) => {
    const newAchievements = [];
    
    // Streak achievements
    if (metrics.currentStreak >= 7 && !achievements.find(a => a.id === 'week_streak')) {
      newAchievements.push({
        id: 'week_streak',
        title: 'Week Warrior',
        description: '7 day streak achieved!',
        icon: '🔥',
        color: '#ff6b6b',
        earned: new Date()
      });
    }
    
    if (metrics.currentStreak >= 30 && !achievements.find(a => a.id === 'month_streak')) {
      newAchievements.push({
        id: 'month_streak',
        title: 'Monthly Master',
        description: '30 day streak achieved!',
        icon: '👑',
        color: '#ffd700',
        earned: new Date()
      });
    }

    // Activity achievements
    if (metrics.totalJournals >= 10 && !achievements.find(a => a.id === 'journal_starter')) {
      newAchievements.push({
        id: 'journal_starter',
        title: 'Journal Explorer',
        description: '10 journal entries completed!',
        icon: '📝',
        color: '#4ecdc4',
        earned: new Date()
      });
    }

    if (metrics.totalMeditations >= 5 && !achievements.find(a => a.id === 'meditation_beginner')) {
      newAchievements.push({
        id: 'meditation_beginner',
        title: 'Mindful Beginner',
        description: '5 meditation sessions completed!',
        icon: '🧘',
        color: '#95e1d3',
        earned: new Date()
      });
    }

    // Level achievements
    if (metrics.level >= 10 && !achievements.find(a => a.id === 'level_10')) {
      newAchievements.push({
        id: 'level_10',
        title: 'Rising Star',
        description: 'Reached level 10!',
        icon: '⭐',
        color: '#667eea',
        earned: new Date()
      });
    }

    // XP achievements
    if (metrics.totalXP >= 1000 && !achievements.find(a => a.id === 'xp_1000')) {
      newAchievements.push({
        id: 'xp_1000',
        title: 'XP Collector',
        description: '1000 XP earned!',
        icon: '💎',
        color: '#764ba2',
        earned: new Date()
      });
    }

    if (newAchievements.length > 0) {
      setAchievements(prev => [...prev, ...newAchievements]);
    }

    return newAchievements;
  }, [achievements]);

  // Active challenges
  const generateChallenges = useCallback(() => {
    const challenges = [
      {
        id: 'daily_trio',
        title: 'Daily Trio',
        description: 'Complete journal, meditation, and conversation in one day',
        progress: 0,
        target: 1,
        reward: 100,
        icon: '🎯',
        color: '#ff6b6b'
      },
      {
        id: 'week_streak',
        title: 'Week Streak',
        description: 'Maintain a 7-day activity streak',
        progress: data.metrics.currentStreak || 0,
        target: 7,
        reward: 200,
        icon: '🔥',
        color: '#ffa726'
      },
      {
        id: 'journal_master',
        title: 'Journal Master',
        description: 'Write 20 journal entries',
        progress: data.metrics.totalJournals || 0,
        target: 20,
        reward: 150,
        icon: '📚',
        color: '#66bb6a'
      },
      {
        id: 'meditation_focus',
        title: 'Meditation Focus',
        description: 'Complete 15 meditation sessions',
        progress: data.metrics.totalMeditations || 0,
        target: 15,
        reward: 120,
        icon: '🧘',
        color: '#42a5f5'
      }
    ];

    setActiveChallenges(challenges);
  }, [data.metrics]);

  useEffect(() => {
    generateChallenges();
  }, [generateChallenges]);

  // Calculate comprehensive metrics with accurate XP matching leaderboard
  const calculateMetrics = useCallback((journals, meditations, conversations) => {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Safe array operations with fallbacks
    const safeJournals = Array.isArray(journals) ? journals : [];
    const safeMeditations = Array.isArray(meditations) ? meditations : [];
    const safeConversations = Array.isArray(conversations) ? conversations : [];

    // Recent activity counts
    const recentJournals = safeJournals.filter(j => j.entry_date && new Date(j.entry_date) >= sevenDaysAgo).length;
    const recentMeditations = safeMeditations.filter(m => m.created_at && new Date(m.created_at) >= sevenDaysAgo).length;
    const recentConversations = safeConversations.filter(c => c.created_at && new Date(c.created_at) >= sevenDaysAgo).length;

    // Monthly activity
    const monthlyJournals = safeJournals.filter(j => j.entry_date && new Date(j.entry_date) >= thirtyDaysAgo).length;
    const monthlyMeditations = safeMeditations.filter(m => m.created_at && new Date(m.created_at) >= thirtyDaysAgo).length;
    const monthlyConversations = safeConversations.filter(c => c.created_at && new Date(c.created_at) >= thirtyDaysAgo).length;

    // Total counts
    const totalJournals = safeJournals.length;
    const totalMeditations = safeMeditations.length;
    const totalConversations = safeConversations.length;
    const totalActivities = totalJournals + totalMeditations + totalConversations;

    // Calculate streak with safe date handling
    let currentStreak = 0;
    try {
      const sortedDates = [...new Set([
        ...safeJournals.map(j => j.entry_date).filter(Boolean),
        ...safeMeditations.map(m => m.created_at ? m.created_at.split('T')[0] : null).filter(Boolean),
        ...safeConversations.map(c => c.created_at ? c.created_at.split('T')[0] : null).filter(Boolean)
      ])].sort().reverse();

      const today = new Date().toISOString().split('T')[0];
      if (sortedDates.includes(today)) {
        currentStreak = 1;
        for (let i = 1; i < sortedDates.length; i++) {
          const prevDate = new Date(sortedDates[i-1]);
          const currDate = new Date(sortedDates[i]);
          const diffDays = (prevDate - currDate) / (1000 * 60 * 60 * 24);
          if (diffDays === 1) {
            currentStreak++;
          } else {
            break;
          }
        }
      }
    } catch (error) {
      console.warn('Error calculating streak:', error);
      currentStreak = 0;
    }

    // Calculate mood metrics safely
    const journalsWithMood = safeJournals.filter(j => j.mood_rating && j.mood_rating > 0);
    const averageMood = journalsWithMood.length > 0 
      ? journalsWithMood.reduce((sum, j) => sum + (j.mood_rating || 0), 0) / journalsWithMood.length 
      : 0;

    // XP calculation (EXACT same as leaderboard API)
    const journalXP = totalJournals * 50;
    const meditationXP = totalMeditations * 30;
    const conversationXP = totalConversations * 20;
    const streakBonus = currentStreak * 10;
    const moodBonus = Math.floor(averageMood * 5);
    const totalXP = journalXP + meditationXP + conversationXP + streakBonus + moodBonus;

    // Level calculation
    const level = Math.floor(totalXP / 100) + 1;
    const progressToNextLevel = (totalXP % 100);

    // Wellness score calculation
    const activityScore = Math.min(50, totalActivities);
    const moodScore = Math.min(25, averageMood * 2.5);
    const streakScore = Math.min(25, currentStreak * 2);
    const wellnessScore = Math.round(activityScore + moodScore + streakScore);

    return {
      totalJournals,
      totalMeditations,
      totalConversations,
      totalActivities,
      recentJournals,
      recentMeditations,
      recentConversations,
      monthlyJournals,
      monthlyMeditations,
      monthlyConversations,
      currentStreak,
      averageMood,
      totalXP,
      level,
      progressToNextLevel,
      wellnessScore,
      journalXP,
      meditationXP,
      conversationXP,
      streakBonus,
      moodBonus
    };
  }, []);

  // Fetch all data and sync with leaderboard API
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      // Fetch all data in parallel with optimized queries
      const [journalsResponse, meditationsResponse, conversationsResponse] = await Promise.all([
        supabase.from('journal_entries')
          .select('entry_date, mood_rating, created_at')
          .eq('user_id', session.user.id)
          .order('entry_date', { ascending: false })
          .limit(1000),
        supabase.from('meditation_sessions')
          .select('created_at, duration')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false })
          .limit(1000),
        supabase.from('conversations')
          .select('created_at, id')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false })
          .limit(1000)
      ]);

      // Handle errors gracefully
      if (journalsResponse.error) {
        console.warn('Journals fetch error:', journalsResponse.error);
      }
      if (meditationsResponse.error) {
        console.warn('Meditations fetch error:', meditationsResponse.error);
      }
      if (conversationsResponse.error) {
        console.warn('Conversations fetch error:', conversationsResponse.error);
      }

      const journals = journalsResponse.data || [];
      const meditations = meditationsResponse.data || [];
      const conversations = conversationsResponse.data || [];

      // Calculate metrics with fallback for any missing data
      const metrics = calculateMetrics(journals, meditations, conversations);
      
      // Update data immediately for fast UI
      setData({ journals, meditations, conversations, metrics });

      // Check for new achievements
      checkAchievements(metrics);

      // Sync XP with leaderboard API in background for accuracy
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/leaderboard/update-xp`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const xpData = await response.json();
          // Update with synced XP values to ensure accuracy
          setData(prev => ({
            ...prev,
            metrics: {
              ...prev.metrics,
              totalXP: xpData.totalXP,
              level: xpData.level || Math.floor(xpData.totalXP / 100) + 1
            }
          }));
        }
      } catch (error) {
        console.warn('Background XP sync failed:', error);
        // Continue with calculated values if API fails
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [session?.user?.id, session?.access_token, calculateMetrics]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get level display with ranks and colors
  const getLevelDisplay = () => {
    const { level } = data.metrics;
    let rankName = 'Explorer';
    let rankIcon = '🌱';
    let rankColor = '#4caf50';
    let gradient = 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)';

    if (level >= 50) {
      rankName = 'Wellness Master';
      rankIcon = '👑';
      rankColor = '#ffd700';
      gradient = 'linear-gradient(135deg, #ffd700 0%, #ffeb3b 100%)';
    } else if (level >= 25) {
      rankName = 'Mindfulness Expert';
      rankIcon = '⭐';
      rankColor = '#ff9800';
      gradient = 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)';
    } else if (level >= 10) {
      rankName = 'Wellness Practitioner';
      rankIcon = '🎯';
      rankColor = '#2196f3';
      gradient = 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)';
    } else if (level >= 5) {
      rankName = 'Growth Apprentice';
      rankIcon = '⚡';
      rankColor = '#9c27b0';
      gradient = 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)';
    }

    return { rankName, rankIcon, rankColor, gradient };
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" gap={3}>
          <Box sx={{ position: 'relative' }}>
            <CircularProgress size={80} thickness={4} sx={{ color: '#667eea' }} />
            <Box sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              fontSize: 24
            }}>
              🧠
            </Box>
          </Box>
          <Typography variant="h5" sx={{ color: '#667eea', fontWeight: 600, textAlign: 'center' }}>
            Analyzing Your Wellness Journey...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 300 }}>
            Calculating XP, tracking progress, and syncing with leaderboard
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          action={<Button color="inherit" size="small" onClick={fetchData} startIcon={<TrendingUp />}>Retry</Button>}
          sx={{ borderRadius: 3, boxShadow: 2 }}
        >
          <Typography variant="h6" fontWeight="600">Failed to load progress data</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Container>
    );
  }

  const { rankName, rankIcon, rankColor, gradient } = getLevelDisplay();

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Amazing Header with Gradient */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        color: 'white',
        py: 4,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated background elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }} />
        
        <Container maxWidth="xl" sx={{ px: 3, position: 'relative' }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <IconButton 
              onClick={() => navigate('/conversation')} 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)', transform: 'scale(1.05)' },
                transition: 'all 0.3s ease'
              }}
            >
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h3" fontWeight="700" sx={{ mb: 1 }}>
                🚀 Your Wellness Journey
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Total XP: {data.metrics.totalXP || 0} • Level {data.metrics.level || 1} {rankIcon} {rankName}
              </Typography>
            </Box>
          </Box>
          
          {/* Achievement Banner */}
          {data.metrics.currentStreak >= 7 && (
            <Box sx={{ 
              bgcolor: 'rgba(255,255,255,0.15)', 
              borderRadius: 3, 
              p: 2, 
              mb: 2,
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <Typography variant="h6" fontWeight="600">
                🔥 INCREDIBLE! {data.metrics.currentStreak} day streak! You're on fire!
              </Typography>
            </Box>
          )}
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4, px: 3, mt: -2, position: 'relative', zIndex: 1 }}>
        
        {/* Date Range Selector & Navigation */}
        <Card sx={{ mb: 4, borderRadius: 4, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              
              {/* Date Range Selector */}
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Typography variant="h6" fontWeight="600" color="#1a1a1a">
                  📊 Analytics Period:
                </Typography>
                <ButtonGroup variant="outlined" size="small">
                  {['day', 'week', 'month', 'year'].map((period) => (
                    <Button
                      key={period}
                      onClick={() => setDateRange(period)}
                      variant={dateRange === period ? 'contained' : 'outlined'}
                      sx={{
                        textTransform: 'capitalize',
                        fontWeight: 600,
                        ...(dateRange === period && {
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white'
                        })
                      }}
                    >
                      {period === 'day' ? 'Today' : 
                       period === 'week' ? 'Week' :
                       period === 'month' ? 'Month' : 'Year'}
                    </Button>
                  ))}
                  <Button
                    onClick={() => setDateRange('custom')}
                    variant={dateRange === 'custom' ? 'contained' : 'outlined'}
                    sx={{
                      fontWeight: 600,
                      ...(dateRange === 'custom' && {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                      })
                    }}
                  >
                    Custom
                  </Button>
                </ButtonGroup>
              </Box>

              {/* Chart Type Selector */}
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body2" fontWeight="600" color="#666">
                  Chart:
                </Typography>
                <ButtonGroup size="small">
                  {[
                    { type: 'line', icon: TrendingUp, label: 'Line' },
                    { type: 'bar', icon: BarChart, label: 'Bar' },
                    { type: 'area', icon: Timeline, label: 'Area' }
                  ].map(({ type, icon: Icon, label }) => (
                    <Button
                      key={type}
                      onClick={() => setChartType(type)}
                      variant={chartType === type ? 'contained' : 'outlined'}
                      startIcon={<Icon />}
                      size="small"
                      sx={{
                        ...(chartType === type && {
                          bgcolor: '#667eea',
                          color: 'white'
                        })
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>
            </Box>

            {/* Custom Date Range Inputs */}
            {dateRange === 'custom' && (
              <Box display="flex" alignItems="center" gap={2} mt={2} flexWrap="wrap">
                <TextField
                  label="Start Date"
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Navigation Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ 
              '& .MuiTab-root': { 
                fontWeight: 600, 
                fontSize: '1rem',
                textTransform: 'none'
              },
              '& .Mui-selected': {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }
            }}
          >
            <Tab 
              icon={<Assessment />} 
              label="Overview" 
              iconPosition="start"
            />
            <Tab 
              icon={<BarChart />} 
              label="Charts" 
              iconPosition="start"
            />
            <Tab 
              icon={<EmojiEvents />} 
              label="Achievements" 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Grid container spacing={4}>
          
          {/* Level & Rank Card - Most Important */}
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ 
              height: '100%', 
              background: gradient,
              color: 'white',
              borderRadius: 4, 
              boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)', 
              position: 'relative',
              overflow: 'hidden',
              '&:hover': { 
                transform: 'translateY(-8px)', 
                boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
              }
            }}>
              {/* Animated background pattern */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 100,
                height: 100,
                opacity: 0.1,
                background: 'radial-gradient(circle, white 20%, transparent 20%)',
                backgroundSize: '20px 20px'
              }} />
              
              <CardContent sx={{ p: 4, textAlign: 'center', position: 'relative' }}>
                <Box sx={{ 
                  width: 80, 
                  height: 80, 
                  borderRadius: '50%', 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: 32, 
                  margin: '0 auto 20px',
                  border: '3px solid rgba(255,255,255,0.3)',
                  animation: 'pulse 2s infinite'
                }}>
                  {rankIcon}
                </Box>
                <Typography variant="h2" fontWeight="900" sx={{ mb: 1, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                  {data.metrics.level || 1}
                </Typography>
                <Typography variant="h5" fontWeight="700" sx={{ mb: 1, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                  {rankName}
                </Typography>
                <Box sx={{ my: 3 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={data.metrics.progressToNextLevel || 0} 
                    sx={{ 
                      height: 12, 
                      borderRadius: 6, 
                      bgcolor: 'rgba(255,255,255,0.3)',
                      '& .MuiLinearProgress-bar': { 
                        bgcolor: 'white', 
                        borderRadius: 6,
                        boxShadow: '0 2px 8px rgba(255,255,255,0.3)'
                      }
                    }} 
                  />
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 600, opacity: 0.9 }}>
                    {data.metrics.progressToNextLevel || 0}% to Level {(data.metrics.level || 1) + 1}
                  </Typography>
                </Box>
                <Chip 
                  label={`${data.metrics.totalXP || 0} Total XP`}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    '& .MuiChip-label': { px: 2 }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Streak Card - Dopamine Hit */}
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ 
              height: '100%', 
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)',
              color: 'white',
              borderRadius: 4, 
              boxShadow: '0 8px 32px rgba(255, 107, 107, 0.3)', 
              '&:hover': { 
                transform: 'translateY(-8px)', 
                boxShadow: '0 12px 40px rgba(255, 107, 107, 0.4)',
                transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
              }
            }}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <LocalFireDepartment sx={{ 
                  fontSize: 64, 
                  mb: 2,
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                  animation: data.metrics.currentStreak >= 3 ? 'bounce 1s infinite' : 'none'
                }} />
                <Typography variant="h2" fontWeight="900" sx={{ mb: 1, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                  {data.metrics.currentStreak || 0}
                </Typography>
                <Typography variant="h5" fontWeight="700" sx={{ mb: 2, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                  Day Streak
                </Typography>
                <Typography variant="h6" fontWeight="600" sx={{ opacity: 0.9 }}>
                  {data.metrics.currentStreak >= 30 ? 'LEGENDARY! 🏆' :
                   data.metrics.currentStreak >= 14 ? 'INCREDIBLE! 🌟' :
                   data.metrics.currentStreak >= 7 ? 'AMAZING! 🎉' : 
                   data.metrics.currentStreak >= 3 ? 'Great job! 💪' : 'Keep going! 🚀'}
                </Typography>
                {data.metrics.streakBonus > 0 && (
                  <Chip 
                    label={`+${data.metrics.streakBonus} Bonus XP`}
                    sx={{ 
                      mt: 2,
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      color: 'white', 
                      fontWeight: 700
                    }}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Activities Card - Progress Tracking */}
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ 
              height: '100%', 
              background: 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)',
              color: 'white',
              borderRadius: 4, 
              boxShadow: '0 8px 32px rgba(78, 205, 196, 0.3)', 
              '&:hover': { 
                transform: 'translateY(-8px)', 
                boxShadow: '0 12px 40px rgba(78, 205, 196, 0.4)',
                transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
              }
            }}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <TrendingUp sx={{ fontSize: 64, mb: 2, filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' }} />
                <Typography variant="h2" fontWeight="900" sx={{ mb: 1, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                  {data.metrics.totalActivities || 0}
                </Typography>
                <Typography variant="h5" fontWeight="700" sx={{ mb: 2, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                  Total Activities
                </Typography>
                <Typography variant="body1" fontWeight="600" sx={{ opacity: 0.9, lineHeight: 1.5 }}>
                  📝 {data.metrics.totalJournals || 0} Journals<br/>
                  🧘 {data.metrics.totalMeditations || 0} Meditations<br/>
                  💬 {data.metrics.totalConversations || 0} Conversations
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Wellness Score Card - Overall Health */}
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ 
              height: '100%', 
              background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
              color: '#333',
              borderRadius: 4, 
              boxShadow: '0 8px 32px rgba(168, 237, 234, 0.3)', 
              '&:hover': { 
                transform: 'translateY(-8px)', 
                boxShadow: '0 12px 40px rgba(168, 237, 234, 0.4)',
                transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
              }
            }}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ 
                  width: 64, 
                  height: 64, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: 28,
                  margin: '0 auto 16px',
                  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)'
                }}>
                  {data.metrics.wellnessScore >= 90 ? '🏆' : 
                   data.metrics.wellnessScore >= 80 ? '💎' : 
                   data.metrics.wellnessScore >= 60 ? '⭐' : 
                   data.metrics.wellnessScore >= 40 ? '📈' : '🎯'}
                </Box>
                <Typography variant="h2" fontWeight="900" sx={{ 
                  mb: 1, 
                  color: data.metrics.wellnessScore >= 80 ? '#10b981' : 
                         data.metrics.wellnessScore >= 60 ? '#667eea' : 
                         data.metrics.wellnessScore >= 40 ? '#fbbf24' : '#ef4444'
                }}>
                  {data.metrics.wellnessScore || 0}
                </Typography>
                <Typography variant="h5" fontWeight="700" sx={{ mb: 2 }}>
                  Wellness Score
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={data.metrics.wellnessScore || 0} 
                  sx={{ 
                    height: 10, 
                    borderRadius: 5, 
                    bgcolor: 'rgba(0,0,0,0.1)', 
                    '& .MuiLinearProgress-bar': { 
                      bgcolor: data.metrics.wellnessScore >= 80 ? '#10b981' : 
                               data.metrics.wellnessScore >= 60 ? '#667eea' : 
                               data.metrics.wellnessScore >= 40 ? '#fbbf24' : '#ef4444', 
                      borderRadius: 5 
                    }
                  }} 
                />
                <Typography variant="body2" fontWeight="700" sx={{ mt: 1, fontSize: '1rem' }}>
                  {data.metrics.wellnessScore >= 90 ? 'Outstanding! 🌟' : 
                   data.metrics.wellnessScore >= 80 ? 'Excellent! 💫' : 
                   data.metrics.wellnessScore >= 60 ? 'Good progress! ✨' : 
                   data.metrics.wellnessScore >= 40 ? 'Getting better! 🌱' : 'Keep improving! 💪'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* XP Breakdown Card */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ 
              height: '100%',
              bgcolor: 'white', 
              borderRadius: 4, 
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid rgba(102, 126, 234, 0.1)',
              '&:hover': { boxShadow: '0 12px 40px rgba(0,0,0,0.12)', transition: 'all 0.3s ease' }
            }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" fontWeight="700" color="#1a1a1a" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Star sx={{ color: '#ffd700' }} />
                  XP Breakdown
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#fff3e0', borderRadius: 3 }}>
                      <Typography variant="h3" fontWeight="700" color="#ff9800">
                        {data.metrics.journalXP || 0}
                      </Typography>
                      <Typography variant="h6" color="#1a1a1a" fontWeight="600">
                        📝 Journal XP
                      </Typography>
                      <Typography variant="body2" color="#666">
                        {data.metrics.totalJournals || 0} × 50 XP
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e8f5e8', borderRadius: 3 }}>
                      <Typography variant="h3" fontWeight="700" color="#4caf50">
                        {data.metrics.meditationXP || 0}
                      </Typography>
                      <Typography variant="h6" color="#1a1a1a" fontWeight="600">
                        🧘 Meditation XP
                      </Typography>
                      <Typography variant="body2" color="#666">
                        {data.metrics.totalMeditations || 0} × 30 XP
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e3f2fd', borderRadius: 3 }}>
                      <Typography variant="h3" fontWeight="700" color="#2196f3">
                        {data.metrics.conversationXP || 0}
                      </Typography>
                      <Typography variant="h6" color="#1a1a1a" fontWeight="600">
                        💬 Chat XP
                      </Typography>
                      <Typography variant="body2" color="#666">
                        {data.metrics.totalConversations || 0} × 20 XP
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#fce4ec', borderRadius: 3 }}>
                      <Typography variant="h3" fontWeight="700" color="#e91e63">
                        {(data.metrics.streakBonus || 0) + (data.metrics.moodBonus || 0)}
                      </Typography>
                      <Typography variant="h6" color="#1a1a1a" fontWeight="600">
                        🎁 Bonus XP
                      </Typography>
                      <Typography variant="body2" color="#666">
                        Streak + Mood
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Activity */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ 
              height: '100%',
              bgcolor: 'white', 
              borderRadius: 4, 
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid rgba(102, 126, 234, 0.1)',
              '&:hover': { boxShadow: '0 12px 40px rgba(0,0,0,0.12)', transition: 'all 0.3s ease' }
            }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" fontWeight="700" color="#1a1a1a" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Timeline sx={{ color: '#667eea' }} />
                  Last 7 Days
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#f8fafc', borderRadius: 3, border: '2px solid #e0e7ff' }}>
                      <Typography variant="h2" fontWeight="900" color="#667eea">
                        {data.metrics.recentJournals || 0}
                      </Typography>
                      <Typography variant="h6" color="#1a1a1a" fontWeight="600">📝 Journals</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#f0fdf4', borderRadius: 3, border: '2px solid #bbf7d0' }}>
                      <Typography variant="h2" fontWeight="900" color="#16a34a">
                        {data.metrics.recentMeditations || 0}
                      </Typography>
                      <Typography variant="h6" color="#1a1a1a" fontWeight="600">🧘 Meditations</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#fef2f2', borderRadius: 3, border: '2px solid #fecaca' }}>
                      <Typography variant="h2" fontWeight="900" color="#dc2626">
                        {data.metrics.recentConversations || 0}
                      </Typography>
                      <Typography variant="h6" color="#1a1a1a" fontWeight="600">💬 Chats</Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                {data.metrics.averageMood > 0 && (
                  <Box mt={3} sx={{ textAlign: 'center', p: 3, bgcolor: '#fffbeb', borderRadius: 3, border: '2px solid #fed7aa' }}>
                    <Typography variant="h2" fontWeight="900" color="#ea580c">
                      {data.metrics.averageMood.toFixed(1)}/10
                    </Typography>
                    <Typography variant="h6" color="#1a1a1a" fontWeight="600">😊 Average Mood</Typography>
                    {data.metrics.moodBonus > 0 && (
                      <Chip 
                        label={`+${data.metrics.moodBonus} Mood Bonus XP`}
                        sx={{ mt: 1, bgcolor: '#ea580c', color: 'white', fontWeight: 600 }}
                      />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Motivational Banner - Full Width */}
          <Grid item xs={12}>
            <Box sx={{ mt: 2 }}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: 4,
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
              }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="700" mb={2}>
                    🎯 You're doing amazing! Keep up the great work!
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Every journal entry, meditation, and conversation brings you closer to your wellness goals.
                  </Typography>
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Button 
                      variant="contained" 
                      size="large"
                      onClick={() => navigate('/journal')}
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.2)', 
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                        fontWeight: 700,
                        px: 4
                      }}
                    >
                      📝 Write Journal (+50 XP)
                    </Button>
                    <Button 
                      variant="contained" 
                      size="large"
                      onClick={() => navigate('/meditation')}
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.2)', 
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                        fontWeight: 700,
                        px: 4
                      }}
                    >
                      🧘 Meditate (+30 XP)
                    </Button>
                    <Button 
                      variant="contained" 
                      size="large"
                      onClick={() => navigate('/conversation')}
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.2)', 
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                        fontWeight: 700,
                        px: 4
                      }}
                    >
                      💬 Chat (+20 XP)
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid>

        </Grid>
        )}

        {/* CHARTS TAB - Advanced Analytics */}
        {activeTab === 1 && (
          <Grid container spacing={4}>
            
            {/* Main Activity Chart */}
            <Grid item xs={12} lg={8}>
              <Card sx={{ borderRadius: 4, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight="700" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChart sx={{ color: '#667eea' }} />
                    Daily Activity Trends
                  </Typography>
                  <Box sx={{ height: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      {chartType === 'line' ? (
                        <LineChart data={generateChartData()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="displayDate" stroke="#666" />
                          <YAxis stroke="#666" />
                          <RechartsTooltip 
                            contentStyle={{ 
                              backgroundColor: 'white', 
                              border: '1px solid #e0e0e0', 
                              borderRadius: 8,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }} 
                          />
                          <Legend />
                          <Line type="monotone" dataKey="journals" stroke="#ff9800" strokeWidth={3} name="📝 Journals" />
                          <Line type="monotone" dataKey="meditations" stroke="#4caf50" strokeWidth={3} name="🧘 Meditations" />
                          <Line type="monotone" dataKey="conversations" stroke="#2196f3" strokeWidth={3} name="💬 Conversations" />
                        </LineChart>
                      ) : chartType === 'bar' ? (
                        <RechartsBarChart data={generateChartData()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="displayDate" stroke="#666" />
                          <YAxis stroke="#666" />
                          <RechartsTooltip 
                            contentStyle={{ 
                              backgroundColor: 'white', 
                              border: '1px solid #e0e0e0', 
                              borderRadius: 8,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }} 
                          />
                          <Legend />
                          <Bar dataKey="journals" fill="#ff9800" name="📝 Journals" />
                          <Bar dataKey="meditations" fill="#4caf50" name="🧘 Meditations" />
                          <Bar dataKey="conversations" fill="#2196f3" name="💬 Conversations" />
                        </RechartsBarChart>
                      ) : (
                        <AreaChart data={generateChartData()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="displayDate" stroke="#666" />
                          <YAxis stroke="#666" />
                          <RechartsTooltip 
                            contentStyle={{ 
                              backgroundColor: 'white', 
                              border: '1px solid #e0e0e0', 
                              borderRadius: 8,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }} 
                          />
                          <Legend />
                          <Area type="monotone" dataKey="totalActivities" stackId="1" stroke="#667eea" fill="#667eea" fillOpacity={0.6} name="Total Activities" />
                        </AreaChart>
                      )}
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* XP Progress Chart */}
            <Grid item xs={12} lg={4}>
              <Card sx={{ borderRadius: 4, boxShadow: '0 8px 32px rgba(0,0,0,0.08)', height: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" fontWeight="700" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp sx={{ color: '#667eea' }} />
                    XP Progress
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={generateChartData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="displayDate" stroke="#666" />
                        <YAxis stroke="#666" />
                        <RechartsTooltip 
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #e0e0e0', 
                            borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          }} 
                        />
                        <Line 
                          type="monotone" 
                          dataKey="totalXP" 
                          stroke="#667eea" 
                          strokeWidth={4} 
                          dot={{ fill: '#667eea', strokeWidth: 2, r: 6 }}
                          name="Daily XP"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                  
                  {/* Quick Stats */}
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box textAlign="center">
                          <Typography variant="h4" fontWeight="700" color="#667eea">
                            {generateChartData().reduce((sum, day) => sum + day.totalXP, 0)}
                          </Typography>
                          <Typography variant="caption" color="#666">
                            Period XP
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box textAlign="center">
                          <Typography variant="h4" fontWeight="700" color="#4caf50">
                            {Math.round(generateChartData().reduce((sum, day) => sum + day.totalXP, 0) / Math.max(generateChartData().length, 1))}
                          </Typography>
                          <Typography variant="caption" color="#666">
                            Avg Daily XP
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Activity Distribution Pie Chart */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 4, boxShadow: '0 8px 32px rgba(0,0,0,0.08)', height: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" fontWeight="700" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PieChart sx={{ color: '#667eea' }} />
                    Activity Distribution
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={[
                            { name: 'Journals', value: data.metrics.totalJournals || 0, fill: '#ff9800' },
                            { name: 'Meditations', value: data.metrics.totalMeditations || 0, fill: '#4caf50' },
                            { name: 'Conversations', value: data.metrics.totalConversations || 0, fill: '#2196f3' }
                          ]}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {[
                            { fill: '#ff9800' },
                            { fill: '#4caf50' },
                            { fill: '#2196f3' }
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Streak Calendar Heatmap */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 4, boxShadow: '0 8px 32px rgba(0,0,0,0.08)', height: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" fontWeight="700" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday sx={{ color: '#667eea' }} />
                    Activity Heatmap
                  </Typography>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(7, 1fr)', 
                    gap: 1, 
                    mt: 2 
                  }}>
                    {generateChartData().slice(-28).map((day, index) => (
                      <Tooltip key={index} title={`${day.displayDate}: ${day.totalActivities} activities`}>
                        <Box sx={{
                          width: 30,
                          height: 30,
                          borderRadius: 1,
                          bgcolor: day.totalActivities === 0 ? '#f5f5f5' :
                                   day.totalActivities === 1 ? '#c8e6c9' :
                                   day.totalActivities === 2 ? '#81c784' :
                                   day.totalActivities >= 3 ? '#4caf50' : '#f5f5f5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: day.totalActivities >= 2 ? 'white' : '#666',
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'scale(1.1)',
                            transition: 'all 0.2s ease'
                          }
                        }}>
                          {day.totalActivities || ''}
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                  <Typography variant="caption" color="#666" sx={{ display: 'block', mt: 2 }}>
                    Last 28 days • Darker = more activities
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Mood Analysis Chart */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ borderRadius: 4, boxShadow: '0 8px 32px rgba(0,0,0,0.08)', height: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight="700" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Psychology sx={{ color: '#ff6b9d' }} />
                    Mood Trends Over Time
                  </Typography>
                  <Box sx={{ height: 300, position: 'relative' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={
                        data.journals
                          .filter(j => j.mood_rating && j.created_at)
                          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                          .map(journal => ({
                            date: new Date(journal.created_at).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            }),
                            mood: journal.mood_rating,
                            fullDate: journal.created_at
                          }))
                          .slice(-30) // Last 30 entries
                      }>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="date" 
                          stroke="#666"
                          fontSize={12}
                          tick={{ fill: '#666' }}
                        />
                        <YAxis 
                          domain={[1, 10]}
                          stroke="#666"
                          fontSize={12}
                          tick={{ fill: '#666' }}
                          label={{ value: 'Mood Rating', angle: -90, position: 'insideLeft' }}
                        />
                        <RechartsTooltip 
                          formatter={(value) => [`${value}/10`, 'Mood Rating']}
                          labelFormatter={(label) => `Date: ${label}`}
                          contentStyle={{
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            border: 'none',
                            borderRadius: 12,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                          }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="mood" 
                          stroke="#ff6b9d" 
                          strokeWidth={3}
                          fill="url(#moodGradient)"
                          dot={{ fill: '#ff6b9d', strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6, stroke: '#ff6b9d', strokeWidth: 2 }}
                        />
                        <defs>
                          <linearGradient id="moodGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ff6b9d" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#ff6b9d" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                    <Typography variant="body2" color="#666" sx={{ textAlign: 'center' }}>
                      � Track your emotional wellness journey over time
                    </Typography>
                    <Typography variant="caption" color="#999" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                      Based on {data.journals.filter(j => j.mood_rating).length} mood entries • Scale: 1-10
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        )}

        {/* ACHIEVEMENTS TAB - Badges & Challenges */}
        {activeTab === 2 && (
          <Grid container spacing={4}>
            
            {/* Earned Achievements */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ borderRadius: 4, boxShadow: '0 8px 32px rgba(0,0,0,0.08)', height: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight="700" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmojiEvents sx={{ color: '#ffd700' }} />
                    Your Achievements ({achievements.length})
                  </Typography>
                  
                  {achievements.length === 0 ? (
                    <Box textAlign="center" py={4}>
                      <Typography variant="h6" color="#666" mb={2}>🎯 No achievements yet!</Typography>
                      <Typography variant="body2" color="#666">
                        Complete activities to unlock your first badge
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {achievements.map((achievement) => (
                        <Grid item xs={12} sm={6} key={achievement.id}>
                          <Card sx={{ 
                            p: 2, 
                            bgcolor: achievement.color + '15',
                            border: `2px solid ${achievement.color}`,
                            borderRadius: 3,
                            '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.3s ease' }
                          }}>
                            <Box display="flex" alignItems="center" gap={2}>
                              <Box sx={{ 
                                fontSize: 32, 
                                width: 60, 
                                height: 60, 
                                borderRadius: '50%', 
                                bgcolor: achievement.color,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {achievement.icon}
                              </Box>
                              <Box flex={1}>
                                <Typography variant="h6" fontWeight="700" color={achievement.color}>
                                  {achievement.title}
                                </Typography>
                                <Typography variant="body2" color="#666">
                                  {achievement.description}
                                </Typography>
                                <Typography variant="caption" color="#999">
                                  Earned: {achievement.earned.toLocaleDateString()}
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Active Challenges */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ borderRadius: 4, boxShadow: '0 8px 32px rgba(0,0,0,0.08)', height: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight="700" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Bolt sx={{ color: '#ff6b6b' }} />
                    Active Challenges
                  </Typography>
                  
                  <Grid container spacing={3}>
                    {activeChallenges.map((challenge) => (
                      <Grid item xs={12} key={challenge.id}>
                        <Card sx={{ 
                          p: 3, 
                          bgcolor: '#f8fafc',
                          border: `2px solid ${challenge.color}20`,
                          borderRadius: 3,
                          '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.1)', transition: 'all 0.3s ease' }
                        }}>
                          <Box display="flex" alignItems="center" gap={2} mb={2}>
                            <Box sx={{ 
                              fontSize: 24, 
                              width: 48, 
                              height: 48, 
                              borderRadius: 2, 
                              bgcolor: challenge.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {challenge.icon}
                            </Box>
                            <Box flex={1}>
                              <Typography variant="h6" fontWeight="700" color="#1a1a1a">
                                {challenge.title}
                              </Typography>
                              <Typography variant="body2" color="#666">
                                {challenge.description}
                              </Typography>
                            </Box>
                            <Chip 
                              label={`${challenge.reward} XP`}
                              sx={{ 
                                bgcolor: challenge.color,
                                color: 'white',
                                fontWeight: 700
                              }}
                            />
                          </Box>
                          
                          <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                              <Typography variant="body2" fontWeight="600" color="#666">
                                Progress: {challenge.progress}/{challenge.target}
                              </Typography>
                              <Typography variant="body2" fontWeight="700" color={challenge.color}>
                                {Math.round((challenge.progress / challenge.target) * 100)}%
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min((challenge.progress / challenge.target) * 100, 100)}
                              sx={{ 
                                height: 8, 
                                borderRadius: 4, 
                                bgcolor: '#e0e4e7',
                                '& .MuiLinearProgress-bar': { 
                                  bgcolor: challenge.color, 
                                  borderRadius: 4 
                                }
                              }} 
                            />
                            {challenge.progress >= challenge.target && (
                              <Chip 
                                label="COMPLETED! 🎉"
                                sx={{ 
                                  mt: 2,
                                  bgcolor: '#4caf50',
                                  color: 'white',
                                  fontWeight: 700,
                                  animation: 'pulse 1s infinite'
                                }}
                              />
                            )}
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Weekly Goals */}
            <Grid item xs={12}>
              <Card sx={{ 
                borderRadius: 4, 
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight="700" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Star sx={{ color: 'white' }} />
                    Weekly Wellness Goals
                  </Typography>
                  
                  <Grid container spacing={3}>
                    {[
                      { label: '📝 Journal Entries', current: data.metrics.recentJournals || 0, target: 5, color: '#ffd700' },
                      { label: '🧘 Meditation Sessions', current: data.metrics.recentMeditations || 0, target: 3, color: '#00e676' },
                      { label: '💬 Conversations', current: data.metrics.recentConversations || 0, target: 7, color: '#ff6b6b' },
                      { label: '🔥 Streak Days', current: Math.min(data.metrics.currentStreak || 0, 7), target: 7, color: '#ff9800' }
                    ].map((goal, index) => (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <Box sx={{ 
                          bgcolor: 'rgba(255,255,255,0.15)', 
                          borderRadius: 3, 
                          p: 3,
                          textAlign: 'center',
                          border: '1px solid rgba(255,255,255,0.2)'
                        }}>
                          <Typography variant="h4" fontWeight="900" sx={{ color: goal.color, mb: 1 }}>
                            {goal.current}/{goal.target}
                          </Typography>
                          <Typography variant="body1" fontWeight="600" sx={{ mb: 2 }}>
                            {goal.label}
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min((goal.current / goal.target) * 100, 100)}
                            sx={{ 
                              height: 6, 
                              borderRadius: 3, 
                              bgcolor: 'rgba(255,255,255,0.2)',
                              '& .MuiLinearProgress-bar': { 
                                bgcolor: goal.color, 
                                borderRadius: 3 
                              }
                            }} 
                          />
                          {goal.current >= goal.target && (
                            <Chip 
                              label="COMPLETED! 🎉"
                              size="small"
                              sx={{ 
                                mt: 2,
                                bgcolor: goal.color,
                                color: 'white',
                                fontWeight: 700
                              }}
                            />
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        )}

      </Container>

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% { transform: translateY(0); }
          40%, 43% { transform: translateY(-15px); }
          70% { transform: translateY(-7px); }
          90% { transform: translateY(-3px); }
        }
      `}</style>
    </Box>
  );
};

export default ProgressDashboard;
